import { toValue } from "vue";
import { until } from "@vueuse/core";
import type { MaybeRefOrGetter } from "vue";
import { createClient as createFetchClient } from "@hey-api/client-fetch";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";

const DEFAULT_BACKEND_URI = import.meta.env.VITE_API_URL;

interface ClientOpts {
    backendURI?: MaybeRefOrGetter<string>;
    requires: {
        auth: boolean;
        organization: boolean;
        stek: boolean;
    };
}

export function createClient(opts?: ClientOpts) {
    return createFetchClient({
        baseUrl: toValue(opts?.backendURI || DEFAULT_BACKEND_URI),
        credentials: "omit",
    });
}

export const useClient = (opts: ClientOpts) => {
    const client = createClient(opts);
    client.interceptors.request.use(async (request) => {
        // It's important these are called inside the interceptor to work.
        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        if (opts.requires.auth) {
            const token = await getToken();
            if (!token) {
                throw Error("Request requires auth, but token not available.");
            }
            request.headers.set("Authorization", `Bearer ${token}`);
        }

        if (opts.requires.organization) {
            await until(organization).not.toBeUndefined({ timeout: 5000 });
            if (!organization.value) {
                throw Error(
                    "Request requires organization, but organization not available."
                );
            }
            request.headers.set("Organization", organization.value!.identity);
        }

        if (opts.requires.stek) {
            await until(stek).not.toBeUndefined({ timeout: 5000 });
            if (!stek.value) {
                throw Error("Request requires stek, but stek not available.");
            }
            request.headers.set("Stek", stek.value!.identity);
        }

        return request;
    });

    return client;
};
